/**
 * The API error contract (T-I18N-02).
 *
 * The API never returns prose a user should read — it returns a stable
 * `code`, and this layer turns that into a `messages.errors.<code>` lookup.
 * That is what lets the backend stay locale-agnostic while the UI stays
 * translated.
 */

/** Mirrors `api/src/uniap/errors.py::ErrorCode`. Kept in sync by a test. */
export const ERROR_CODES = [
  'unauthenticated',
  'csrf',
  'rate_limited',
  'not_found',
  'conflict',
  'validation',
  'quiz_incomplete',
  'quiz_unknown_item',
  'profile_step_required',
  'invalid_major',
  'internal'
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

export class ApiError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly field?: string
  readonly detail?: string
  readonly retryAfter?: number

  constructor(init: {
    code: ErrorCode
    status: number
    field?: string
    detail?: string
    retryAfter?: number
  }) {
    super(init.detail ?? init.code)
    this.name = 'ApiError'
    this.code = init.code
    this.status = init.status
    this.field = init.field
    this.detail = init.detail
    this.retryAfter = init.retryAfter
  }

  /** The message key the UI should render. Unknown codes fall back to `internal`. */
  get messageKey(): ErrorCode {
    return (ERROR_CODES as readonly string[]).includes(this.code) ? this.code : 'internal'
  }
}

/**
 * Build an `ApiError` from a failed response.
 *
 * A non-JSON body (a proxy error page, a truncated response) still has to
 * produce a typed error rather than throwing inside the error path, so the
 * parse is guarded and falls back to the status code.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  let body: Record<string, unknown> = {}
  try {
    body = (await response.json()) as Record<string, unknown>
  } catch {
    body = {}
  }
  const code = typeof body.code === 'string' ? body.code : statusToCode(response.status)
  return new ApiError({
    code: code as ErrorCode,
    status: response.status,
    field: typeof body.field === 'string' ? body.field : undefined,
    detail: typeof body.detail === 'string' ? body.detail : undefined,
    retryAfter: typeof body.retry_after === 'number' ? body.retry_after : undefined
  })
}

function statusToCode(status: number): ErrorCode {
  if (status === 401) return 'unauthenticated'
  if (status === 403) return 'csrf'
  if (status === 404) return 'not_found'
  if (status === 409) return 'conflict'
  if (status === 422) return 'validation'
  if (status === 429) return 'rate_limited'
  return 'internal'
}
